import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import billingService from '../../services/billingService';
import practiceService from '../../services/practiceService';
import { toast } from 'react-toastify';
import SearchBar from '../../components/Common/SearchBar/SearchBar';
import Table from '../../components/Common/Table/Table';
import Heading from '../../components/Common/Heading/Heading';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement.css';
import './BillingCycleReport.css';
import { matchesWordStartAny } from '../../utils/searchMatch';

const BillingCycleReport = () => {
  const { practiceId } = useParams();
  const navigate = useNavigate();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    return () => clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportDates, setReportDates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPractice();
    fetchReportDates();
  }, [practiceId]);

  const fetchPractice = async () => {
    try {
      const response = await practiceService.getPractice(practiceId);
      if (response.data && response.data.success) {
        const practiceData = response.data.data;
        setPractice(practiceData);
        updateBreadcrumbs([
          { label: 'Billing', path: '/billing' },
          { label: `${practiceData.practice_name || 'Practice'} - Billing Cycle Report`, path: null, active: true }
        ]);
      }
    } catch (error) {
      console.error('Error fetching practice:', error);
    }
  };

  const fetchReportDates = async () => {
    if (!practiceId) return;

    try {
      setLoading(true);
      const response = await billingService.getBillingReports(practiceId);
      if (response.success && response.data.reportDates) {
        setReportDates(response.data.reportDates);
      }
    } catch (error) {
      console.error('Error fetching report dates:', error);
      toast.error('Failed to load billing cycle report');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const filteredReportDates = reportDates.filter(rd => {
    if (!searchTerm) return true;
    return matchesWordStartAny(
      [
        formatDate(rd.rpm_start_date),
        formatDate(rd.rpm_end_date),
        rd.note,
        rd.number_of_days,
      ],
      searchTerm
    );
  });

  return (
    <div className="billing-cycle-report-feature">
      <div className="list-screen-page-container scm-page billing-cycle-report-wrapper">
        <div className="scm-page-header">
          <h2 className="dashboard-title">Billing Cycle Report</h2>
        </div>
        <div className="scm-list-frame">
          <div className="scm-list-panel">
            <div className="scm-list-body">
              <div className="scm-header-row">
                <div className="scm-header-actions">
                  <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search report dates..."
                    label={null}
                    className="scm-search-wrapper"
                  />
                </div>
              </div>

              <div className="practice-tab-table-card">
                <Table
                  columns={[
                    {
                      key: 'rowNo',
                      title: '#',
                      width: '56px',
                      className: 'reusable-table-centered-col',
                      render: (_row, rowIndex) => rowIndex + 1
                    },
                    { key: 'rpm_start_date', title: 'RPM Start Date', width: '20%', render: (row) => formatDate(row.rpm_start_date) },
                    { key: 'rpm_end_date', title: 'RPM End Date', width: '20%', render: (row) => formatDate(row.rpm_end_date) },
                    { key: 'note', title: 'Billing Notes', render: (row) => row.note || 'Empty Note' },
                    { key: 'number_of_days', title: 'Number of Days', width: '15%', className: 'reusable-table-centered-col', render: (row) => row.number_of_days || 0 }
                  ]}
                  data={filteredReportDates}
                  isLoading={loading}
                  loadingMode="skeleton"
                  skeletonRows={10}
                  emptyMessage="No billing cycle data available"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingCycleReport;

